import React, { Component } from 'react';
import { array, arrayOf, func, node, number, object, oneOf, string } from 'prop-types';
import classNames from 'classnames';

import { injectIntl, intlShape } from '../../../util/reactIntl';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';
import { languagesHelper as language } from '../../../helpers/languages';
import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectGroupMultipleFilter.module.css';

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, textClassName, withIcon, isDe } = props;
  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {options.map((option, index) => {
          const fieldId = `${id}.${option.key}`;
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox
                id={fieldId}
                name={name}
                label={isDe ? option.labelDe : option.label}
                value={option.key}
                textClassName={textClassName}
                withIcon={withIcon}
              />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

const groupFormat = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

class SelectGroupMultipleFilter extends Component {
  constructor(props) {
    super(props);

    this.filter = null;
    this.filterContent = null;

    this.positionStyleForContent = this.positionStyleForContent.bind(this);
  }

  positionStyleForContent() {
    if (this.filter && this.filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - this.filter.getBoundingClientRect().right;
      const labelWidth = this.filter.offsetWidth;
      const contentWidth = this.filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const contentPlacementOffset = this.props.contentPlacementOffset;

      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }

  render() {
    const {
      rootClassName,
      className,
      id,
      name,
      label,
      options,
      initialValues,
      contentPlacementOffset,
      onSubmit,
      queryParamNames,
      schemaType,
      searchMode,
      intl,
      showAsPopup,
      enumOptionsOrder,
      ...rest
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const queryParamName = enumOptionsOrder.map(p => `pub_${p.filter}`);

    const namedInitialValues = Object.fromEntries(
      Object.entries(initialValues)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key.replace(/^pub_/, ''), value.split(',')])
    );

    const hasInitialValues = !!initialValues && Object.values(namedInitialValues).length;

    const selectedCount = Object.values(namedInitialValues).reduce(
      (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
      0
    );

    const labelForPopup = hasInitialValues
      ? intl.formatMessage(
          { id: 'SelectMultipleFilter.labelSelected' },
          { labelText: label, count: selectedCount }
        )
      : label;

    const labelSelectionForPlain = hasInitialValues
      ? intl.formatMessage(
          { id: 'SelectMultipleFilterPlainForm.labelSelected' },
          { count: selectedCount }
        )
      : '';

    const contentStyle = this.positionStyleForContent();

    // pass the initial values with the name key so that
    // they can be passed to the correct field

    const handleSubmit = values => {

      if (!values) {
        const clearObject = {};
        queryParamName.forEach(param => (clearObject[param] = undefined));
        return onSubmit(clearObject);
      }

      const result = {};
      for (const [key, params] of Object.entries(values)) {
        if (params.length === 0) {
          result[`pub_${key}`] = undefined;
        } else {
          const res = format(params, key, 'multi-enum', searchMode);
          result[`pub_${key}`] = res[key];
        }
      }

      onSubmit(result);
    };

    let currentLocale = language.get();
    const isDe = currentLocale === "de";

    return showAsPopup ? (
      <FilterPopup
        customPopup={css.popupPosition}
        className={classes}
        rootClassName={rootClassName}
        popupClassName={css.popupSize}
        name={name}
        label={labelForPopup}
        isSelected={hasInitialValues}
        id={`${id}.popup`}
        showAsPopup
        contentPlacementOffset={contentPlacementOffset}
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        keepDirtyOnReinitialize
        {...rest}
      >
        <div className={css.groups}>
          {enumOptionsOrder.map(option => {
            const createFilterOptions = options =>
              options.map(o => ({ key: `${o.option}`, label: o.label, labelDe: o.labelDe || o.label }));

            return (
              <div className={css.groupContainer}>
                {option?.label ? (
                  <label htmlFor={`${id}-checkbox-group`}>{isDe ? option.labelDe : option.label}</label>
                ) : null}
                <GroupOfFieldCheckboxes
                  className={css.fieldGroup}
                  textClassName={css.text}
                  name={option.filter}
                  id={`${id}-checkbox-group`}
                  options={createFilterOptions(options?.[option?.filter])}
                  isDe={isDe}
                />
              </div>
            );
          })}
        </div>
      </FilterPopup>
    ) : (
      <FilterPlain
        className={className}
        rootClassName={rootClassName}
        label={label}
        labelSelection={labelSelectionForPlain}
        isSelected={hasInitialValues}
        id={`${id}.plain`}
        liveEdit
        contentPlacementOffset={contentStyle}
        onSubmit={handleSubmit}
        initialValues={namedInitialValues}
        {...rest}
      >
        <div className={css.groupsMobile}>
          {enumOptionsOrder.map(option => {
            const createFilterOptions = options =>
              options.map(o => ({ key: `${o.option}`, label: o.label, labelDe: o.labelDe || o.label }));

            return (
              <div className={css.groupContainerMobile}>
                {option?.label ? (
                  <label htmlFor={`${id}-checkbox-group`}>{option.label}</label>
                ) : null}
                <GroupOfFieldCheckboxes
                  className={css.fieldGroupPlain}
                  textClassName={css.text}
                  name={option.filter}
                  id={`${id}-checkbox-group`}
                  options={createFilterOptions(options?.[option?.filter])}
                  isDe={isDe}
                />
              </div>
            );
          })}
        </div>
      </FilterPlain>
    );
  }
}

SelectGroupMultipleFilter.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  contentPlacementOffset: 0,
  searchMode: null,
};

SelectGroupMultipleFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  enumOptionsOrder: arrayOf(string).isRequired,
  label: node.isRequired,
  onSubmit: func.isRequired,
  options: array.isRequired,
  searchMode: oneOf(['has_all', 'has_any']),
  schemaType: oneOf([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM]).isRequired,
  initialValues: object,
  contentPlacementOffset: number,

  // form injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SelectGroupMultipleFilter);
